import { useEffect, useMemo, useRef, useState } from 'react';
import { noteSettled } from '../diagnostics/beatLog';
import { beatOf, fallHeadingOf, phaseAt } from '../engine/display/beats';
import useDisplayClock from './useDisplayClock';

const NO_STARTS = new Map();
/**
 * How long a seen impact is remembered after the live list lets it go, ms: longer than any beat (Undying's fall ends
 * near 3.4 s), shorter than anyone would notice a stale one. The live list prunes at IMPACT_TTL_MS (2.5 s), which cut
 * a long burst's rattle and Undying's rise short.
 */
const KEEP_MS = 6000;
const NO_LOOKS = {};
const NO_FALLS = Object.freeze({});
const wholeBeat = (b) => b.ms;
/** The frame a screen that holds still shows of a beat: its landing. */
const STILL = { phase: /** @type {const} */ ('impact'), k: 0.5 };

/**
 * useDisplayBeats - the beats this screen plays now (plan map-display, D).
 *
 * Each live impact (useShellImpacts: the newest per combatant, a few seconds
 * long) becomes a beat (`engine/display/beats`) timed from the moment THIS
 * screen first saw it, not from the attacker's clock: a remote screen hears
 * of a shot a little late and would otherwise open on the middle of it. A
 * beat plays to its last piece even once the live list has let its impact go
 * (unless a newer attack on the same target overtakes it). The display clock
 * runs only while a beat is playing; a screen that holds still shows each live
 * impact's beat, unanimated, for as long as the impact lives.
 *
 * @param {Record<string, import('../engine/impacts').Impact | undefined> | undefined} impacts - the live impacts by combatant
 * @param {Readonly<Record<string, import('../engine/battlefield/tokens').Token>>} tokens - this screen's markers by id
 * @param {boolean} still - the viewer's motion setting: no animation
 * @param {Readonly<Record<string, readonly import('../engine/display/looks').MapLook[]>>} [looks] - each figure's looks
 *   (a Warp Instability target backtracks when struck)
 * @returns {{ beat: import('../engine/display/beats').Beat, phase: import('../engine/display/beats').BeatPhase | null, k: number, t: number | null }[]}
 *   the beats on screen, where each is (no phase while only its burst plays on), and how far in (ms); a still screen
 *   gets no time, and its burst shows whole
 */
export default function useDisplayBeats(impacts, tokens, still, looks = NO_LOOKS) {
  return useBeatFrame(useBeats(impacts, tokens, still, looks), still);
}

/**
 * WHICH BEATS EXIST (the Track K simplify): each impact this screen has seen, as its beat (engine/display/beats),
 * timed from when this screen first saw it. Computed once, in the panel (battlefieldPanel/useBeatsOnMap), and
 * handed to whoever draws or moves by them; the fuzzer's beat oracle is told here which beats started.
 */
export function useBeats(impacts, tokens, still, looks = NO_LOOKS) {
  const starts = useStarts(impacts);
  const beats = useMemo(
    () =>
      [...starts.values()].flatMap(({ at, impact, live }) => {
        // Holding still shows the live impacts only: a kept beat has no clock to end it.
        if (still && !live) return [];
        const warps = looks[impact.combatantId]?.includes('warpinst') ?? false;
        const beat = beatOf({ ...impact, at }, tokens, warps);
        return beat ? [beat] : [];
      }),
    [tokens, starts, looks, still],
  );
  // The fuzzer's beat oracle reads which beats this screen started (engine/fuzz/beats).
  // Keyed on the starts, not the beats: those are rebuilt whenever a marker moves.
  useEffect(() => {
    for (const id of starts.keys()) noteSettled(id);
  }, [starts]);
  return beats;
}

/**
 * WHERE EACH BEAT IS NOW: its own clock while any beat is within `until` of its start (the whole beat by default;
 * the panel's only while a beat moves something, `beat.movesMs`), and each beat's phase and time. The list is the
 * same object between frames, so a render that is not the clock's reuses it.
 */
export function useBeatFrame(beats, still, until = wholeBeat) {
  const [now, setNow] = useState(() => Date.now());
  const playing = !still && beats.some((b) => now < b.at + until(b));
  useDisplayClock(playing, () => setNow(Date.now()));
  return useMemo(
    () =>
      beats.flatMap((beat) => {
        if (still) return [{ beat, ...STILL, t: null }];
        // A beat seen after the clock's last frame starts now, not "not yet".
        const t = Math.max(now, beat.at) - beat.at;
        // Kept to its end: past its last phase while its burst plays on (drawn by time; no phase then).
        return t < beat.ms ? [{ beat, ...phaseAt(beat, beat.at + t), t }] : [];
      }),
    [beats, now, still],
  );
}

/**
 * @typedef {{ at: number, heading: number, was: number | undefined }} Fall - a figure's fall: when this screen saw
 *   the blow (ms), the heading it lies along, and the heading its token had then
 */

/**
 * WHICH WAY EACH FIGURE FELL (plan design-gaps, S4): the heading of the latest killing blow this screen saw on each
 * figure, away from its attacker (`fallHeadingOf`), kept past the beat for as long as the panel lives, so the figure
 * lies where the blow threw it. Never stored or synced: a screen that did not see the blow (one that reloads, or
 * joins later) lies the figure along its own heading. A fall is dropped once its figure is no longer dead and the
 * blow is older than a beat (KEEP_MS: the kill's roster write and its impact arrive within that), so a figure
 * revived and later killed by hand lies along its heading (the Track S review). The marker uses the fall only while
 * the token's stored heading is still the one it fell with (`was`): a body someone turns lies as they turned it.
 *
 * @param {readonly import('../engine/display/beats').Beat[]} beats - the beats this screen has (useBeats)
 * @param {Readonly<Record<string, { posture: string }>>} models - each figure's model (its posture)
 * @param {Readonly<Record<string, { heading?: number }>>} tokens - the stored tokens, by id
 * @returns {Readonly<Record<string, Fall>>} by the fallen figure's id
 */
export function useFalls(beats, models, tokens) {
  const [falls, setFalls] = useState(NO_FALLS);
  const latest = useRef(falls);
  useEffect(() => {
    // Only a real change is queued: an updater that returns the same record still costs a render to find that out,
    // and the beats are rebuilt whenever a marker moves.
    const next = withFalls(latest.current, beats, models, tokens, Date.now());
    if (next === latest.current) return;
    latest.current = next;
    setFalls(next);
  }, [beats, models, tokens]);
  return falls;
}

/** The falls with each new killing blow added and each one a revival has outlived dropped; `prev` when unchanged. */
export function withFalls(prev, beats, models, tokens, now) {
  return withoutRevived(withNewFalls(prev, beats, tokens), models, now);
}

/** Each killing blow newer than the fall its figure has, as that figure's fall. */
function withNewFalls(prev, beats, tokens) {
  let next = prev;
  for (const b of beats) {
    const heading = fallHeadingOf(b);
    if (heading === null || (next[b.targetId]?.at ?? -Infinity) >= b.at) continue;
    next = { ...next, [b.targetId]: { at: b.at, heading, was: tokens[b.targetId]?.heading } };
  }
  return next;
}

/** Without the falls of figures no longer dead once the blow is older than a beat (the kill is landing within it). */
function withoutRevived(falls, models, now) {
  const gone = Object.keys(falls).filter((id) => models[id]?.posture !== 'dead' && now - falls[id].at >= KEEP_MS);
  if (!gone.length) return falls;
  const next = { ...falls };
  for (const id of gone) delete next[id];
  return next;
}

/**
 * Each impact this screen has seen, by id: when it first saw it (ms, Date.now), the impact, and whether it is still
 * live. A gone one is kept for KEEP_MS, unless a newer one on the same target has overtaken it.
 */
function useStarts(impacts) {
  const [starts, setStarts] = useState(NO_STARTS);
  useEffect(() => {
    const live = Object.values(impacts ?? {}).filter((i) => !!i);
    setStarts((prev) => {
      const now = Date.now();
      const next = new Map(live.map((i) => [i.id, { at: prev.get(i.id)?.at ?? now, impact: i, live: true }]));
      for (const [id, seen] of prev) if (keptBeside(next, id, seen, now)) next.set(id, { ...seen, live: false });
      return sameStarts(prev, next) ? prev : next;
    });
  }, [impacts]);
  return starts;
}

/** A gone impact stays while its beat may still play and nothing newer struck the same target. */
const keptBeside = (next, id, seen, now) =>
  !next.has(id) &&
  now - seen.at < KEEP_MS &&
  ![...next.values()].some((n) => n.live && n.impact.combatantId === seen.impact.combatantId);

const sameStarts = (prev, next) =>
  next.size === prev.size &&
  [...next].every(([id, n]) => prev.get(id)?.impact === n.impact && prev.get(id)?.live === n.live);
